<template>
  <div class='data-leak-issue-body'>
    <div class="description-block">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container description">
        {{description}}
      </div>
    </div>
    <div class="tld-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <span class="solution-block" v-if='leakMitigation.length'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
        <div v-for='(solution, index) in leakMitigation' :key='index'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
         <div class='text-container'>{{solution}}</div>
        </div>
      </div>
    </span>
    <div class="technical-block">
      <div class="data-title">{{$t('issueDataPopup.technical.data')}}</div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='technical-data' v-if='leakUrl'>
            <div class="data-sub-title">{{$t('issueDataPopup.data.leak.url')}}</div>
            <div class='text-container leak-url'>
              <a :href='leakUrl' target="_blank"> {{ leakUrl }} </a>
            </div>
          </div>
          <div class='technical-data' v-if='leakValue && leakValue !== "[]" && leakValue.length > 0'>
            <div class="data-sub-title">{{$t('issueDataPopup.data.leak.value')}}</div>
            <div class='text-container leak-value' v-html="leakValueFormatted"></div>
          </div>
        </div>
      </div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='details-column technical-data' v-if='leakBy'>
            <div class="data-sub-title">{{$t('issueDataPopup.data.leak.by')}}</div>
            <div class='text-container leak-by'>{{ leakBy }}</div>
          </div>
          <div class='details-column technical-data' v-if='leakType'>
            <div class="data-sub-title">{{$t('issueDataPopup.data.leak.type')}}</div>
            <div class='text-container leak-type'>{{ leakType }}</div>
          </div>
          <div class='details-column technical-data' v-if='leakTime'>
            <div class="data-sub-title">{{$t('issueDataPopup.data.leak.time')}}</div>
            <div class='text-container leak-time'>{{ leakTime }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

export default {
  name: 'DataLeakIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  computed: {
    tld() {
      return this.issue.tld;
    },
    metaData() {
      return this.issue.metaData;
    },
    description() {
      return this.metaData.leakDetails?.leak_description || this.issue.description;
    },
    leakDetails() {
      return this.metaData.leakDetails;
    },
    leakValue() {
      return this.leakDetails.leak_value || this.leakDetails.line_string;
    },
    leakValueFormatted() {
      if (Array.isArray(this.leakValue)) {
        return `<ul class='issue-data-leaks-list'>${this.leakValue.map(leak => ` <li> <code>${leak.replace(/['"[\]]/g, '')} </code></li > `).join('')}</ul>`;
      }
      return `<code>${this.leakValue.replace(/['"[\]]/g, '')}</code>`;
    },
    leakUrl() {
      return this.leakDetails.leak_url;
    },
    leakBy() {
      return this.leakDetails.leak_by;
    },
    leakTime() {
      return this.leakDetails.leak_time;
    },
    leakType() {
      return this.leakDetails.leak_type;
    },
    leakMitigation() {
      let mitigation = this.leakDetails?.leak_mitigation || [];
      if (!Array.isArray(mitigation)) {
        const jsonStringArray = mitigation.replace(/'/g, '"');
        mitigation = JSON.parse(jsonStringArray);
      }
      return mitigation || [];
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.data-leak-issue-body {
  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(650);
    margin-right: $space-4;
  }
  .description-block {
    display: block;
    width: rem(650);
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: $space-15;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-5;
    }
    .description {
      white-space: pre-line;
    }
  }
  .solution-block {
    display: block;
    padding-bottom: $space-2;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
    }
    .mitigation-option {
      display: flex;
      margin-bottom: $space-2;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .technical-block {
    padding-bottom: $space-5;
    display: block;
    width: rem(650);
    .url-section {
      .bullet {
        padding-right: rem(5);
      }
      .text-container {
        padding-left: rem(5);
        padding-right: $space-15;
        color: var(--text-primary);
        font-size: $font-size-base;
        letter-spacing: 0;
        line-height: rem(18);
        padding-bottom: $space-4;
        max-width: rem(700);
      }
      display: flex;
      .material-icons {
        font-size: rem(15);
        opacity: 0.5;
        margin-top: rem(3);
      }
    }
    .technical-data {
      padding-top: $space-1;
    }
  }
  .cvss-score {
    text-align: center;
    font-family: $font-family-semibold;
    font-size: $font-size-base;
    justify-content: center;
    letter-spacing: 0;
    line-height: rem(16);
    padding: rem(5) rem(5) rem(5) rem(6);
  }

  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-minor-sub-title  {
    font-size: rem(8);
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .tld-block {
    margin-bottom: $space-5;
  }
}
</style>
<style lang='scss'>
.issue-data-leaks-list{
  li:before {
    content: 'lens_blur';
    font-family: 'Material Icons', serif;
    font-size: 15px;
    opacity: 0.5;
    margin-right: 5px;
  }
  li {
    padding-bottom: 5px;
    display: flex;
    align-items: center;
  }
}
</style>
